/**
 * Freight priced at checkout by the freight API (Warren, ruling B, 21 October 2026). The pieces the Api, Admin and the
 * shop share: the one line the customer sees, the manual line, the price rule, the delivery questions and the catalog's
 * shipping fields. No I/O here; the carrier lives in apps/api/src/lib/freight.
 *
 * The price (Warren's hardware quote runbook): the customer pays the carrier rate divided by 0.756, a 24.4% margin, the
 * same as hardware, shown as ONE line, "Hardware shipped to location". Never cost plus markup. The buffer percentage
 * Warren sets in Admin settings applies on top of that price. Cargo insurance on a shipment with a DC unit is part of the
 * carrier cost before the division.
 */
/** The one line the customer sees for freight (Warren, 21 October 2026). */
export const FREIGHT_LINE = 'Hardware shipped to location';
/** What a job shows when the freight API cannot price it (Warren, 21 October 2026): ops quotes it on the one business
 * day clock and the pay link follows. */
export const FREIGHT_MANUAL_LINE = 'Freight quote within one business day';
/** Price = carrier cost / 0.756: a 24.4% margin, the same as hardware. */
export const FREIGHT_MARGIN_DIVISOR = 0.756;
/** The largest carrier cost priced, in cents ($1,000,000): keeps the integer arithmetic exact. */
export const FREIGHT_MAX_COST_CENTS = 100_000_000;
/**
 * The customer's freight price in cents: the carrier cost (rate plus any cargo insurance) divided by 0.756, then the
 * buffer on top, rounded half up once at the end. Integer arithmetic: cost x 1000 x (10000 + buffer basis points) /
 * (756 x 10000), so $378.41 at a 10% buffer is exactly $550.60.
 */
export function freightPriceCents(carrierCostCents, bufferPct = 0) {
    const cost = Math.min(FREIGHT_MAX_COST_CENTS, Math.max(0, Math.round(carrierCostCents)));
    const bp = Math.max(0, Math.round((bufferPct ?? 0) * 100));
    const num = cost * 1000 * (10000 + bp), den = 756 * 10000;
    const q = Math.floor(num / den), r = num - q * den;
    return 2 * r >= den ? q + 1 : q;
}
/** The carrier cost a price paid covers at the full margin (price x 0.756, rounded down): a booking that costs more is
 * beyond the buffer, and EVI absorbs the difference on that order. */
export function freightCoveredCostCents(priceCents) {
    return Math.floor(Math.max(0, Math.round(priceCents)) * 756 / 1000);
}
/** The delivery questions, asked once on the job in plain words (Warren, 21 October 2026). */
export const DELIVERY_QUESTIONS = {
    loading_dock: 'Loading dock at the site?',
    limited_access: 'Limited access (construction site, garage)?',
    appointment: 'Delivery appointment',
};
/** The carrier's delivery services for a job's answers: no dock means liftgate-delivery, limited access adds
 * limited-access-delivery, and a job site takes delivery-appointment unless switched off. */
export function deliveryServices(d) {
    const out = [];
    if (d?.loading_dock !== true)
        out.push('liftgate-delivery');
    if (d?.limited_access === true)
        out.push('limited-access-delivery');
    if (d?.appointment !== false)
        out.push('delivery-appointment');
    return out;
}
export const PRODUCT_SHIPPING_LABELS = {
    weight_lbs: 'weight', length_in: 'length', width_in: 'width', height_in: 'height', units_per_pallet: 'units per pallet',
};
const positive = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;
/** A SKU's cartons: its list when it has one, else its one carton (the fields above), else none. */
export function cartonsOf(s) {
    if (s?.cartons?.length)
        return s.cartons;
    if (!s)
        return [];
    const one = { weight_lbs: s.weight_lbs ?? null, length_in: s.length_in ?? null, width_in: s.width_in ?? null, height_in: s.height_in ?? null,
        units_per_pallet: s.units_per_pallet ?? null, parcel: !!s.parcel };
    return Object.values(one).some(v => v !== null && v !== false) ? [one] : [];
}
/** The fields a SKU still needs before the freight API can price it: each carton's weight and dimensions (an empty
 * pallet rule derives the pallet). Empty: ready. */
export function shippingMissing(s) {
    const cartons = cartonsOf(s), out = [];
    if (!cartons.length)
        return ['weight_lbs', 'length_in', 'width_in', 'height_in'].map(k => PRODUCT_SHIPPING_LABELS[k]);
    cartons.forEach((c, i) => {
        for (const k of ['weight_lbs', 'length_in', 'width_in', 'height_in'])
            if (!positive(c[k]))
                out.push(`${cartons.length > 1 ? `carton ${i + 1} ` : ''}${PRODUCT_SHIPPING_LABELS[k]}`);
    });
    return out;
}
/** The provisional ship-from (Warren, 21 October 2026: Autel, 8420 Triad Drive, Greensboro NC 27409; Warren is
 * confirming). Admin's freight setting replaces it. */
export const PROVISIONAL_FREIGHT_ORIGIN = {
    name: 'Autel', street: '8420 Triad Drive', street2: '', city: 'Greensboro', state: 'NC', zip: '27409', country: 'US',
    time_zone: 'America/New_York',
};
//# sourceMappingURL=freight.js.map